/**
 * Merci : l'index des marques.
 *
 * Deux travaux, tous deux de l'affinage : la page rendue par le serveur est
 * complète et navigable sans ce script.
 *
 *   - **la fusion des pages.** Quand Shopify ne rend pas toutes les fiches en
 *     une fois, la section produit de vraies pages (`?page=2`…). Le script
 *     charge les autres par le rendu de section, réunit les marques, les
 *     re-trie et les re-groupe par lettre, puis masque les liens de page.
 *   - **les onglets.** Chaque marque porte ses catégories dans
 *     `data-categories` ; un onglet masque celles qui n'en sont pas, puis les
 *     lettres restées vides, dans la liste comme dans l'alphabet.
 *
 * La clé de tri est celle du serveur (`data-key`, le nom passé par
 * `handleize`), comparée octet par octet comme le filtre `sort` de Liquid :
 * une page fusionnée se range exactement comme une page rendue d'un bloc.
 *
 * Le markup des groupes reprend `snippets/merci-brand-group-open.liquid` :
 * toute classe changée là-bas est à reporter ici.
 */

const CHIFFRES = '0-9';

/** La lettre de rangement d'une clé : son initiale, ou « 0-9 ». */
function lettreDe(cle) {
  const initiale = cle.charAt(0);
  return /[0-9]/.test(initiale) ? CHIFFRES : initiale.toUpperCase();
}

class MerciBrandIndex extends HTMLElement {
  connectedCallback() {
    this.groupes = this.querySelector('.merci-brands__groups');
    this.onglets = [...this.querySelectorAll('.merci-brands__tab')];
    this.lettres = [...this.querySelectorAll('.merci-brands__letter-link')];

    for (const onglet of this.onglets) {
      onglet.addEventListener('click', () => this.#choisir(onglet));
    }

    const pages = Number(this.dataset.pages) || 1;
    if (pages > 1) this.#fusionner(pages);
  }

  /** Charge les autres pages et rebâtit la liste entière. */
  async #fusionner(pages) {
    const courante = Number(this.dataset.page) || 1;
    // Pas forcément `page` : sur une liste de metaobjets, Shopify nomme le
    // paramètre d'après la liste (`page_1a5f76a0`) et ignore `page`.
    const parametre = this.dataset.pageParam || 'page';
    const requetes = [];

    for (let page = 1; page <= pages; page++) {
      if (page === courante) continue;
      const url = new URL(window.location.href);
      url.hash = '';
      url.searchParams.set(parametre, String(page));
      url.searchParams.set('section_id', this.dataset.sectionId);
      requetes.push(
        fetch(url).then((reponse) => (reponse.ok ? reponse.text() : Promise.reject(reponse.status)))
      );
    }

    let pagesHtml;
    try {
      pagesHtml = await Promise.all(requetes);
    } catch {
      // Une page manque : on garde la page du serveur et ses liens, plutôt
      // qu'une liste fusionnée à laquelle il manquerait des marques.
      return;
    }

    const marques = new Map();
    for (const item of this.querySelectorAll('.merci-brands__item')) {
      marques.set(item.dataset.handle, item);
    }
    for (const html of pagesHtml) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      for (const item of doc.querySelectorAll('.merci-brands__item')) {
        if (!marques.has(item.dataset.handle)) {
          marques.set(item.dataset.handle, document.importNode(item, true));
        }
      }
    }

    this.#rebatir([...marques.values()]);
    this.querySelector('.merci-brands__pagination')?.setAttribute('hidden', '');
    this.#appliquer();
  }

  /** Range les marques par clé, puis par lettre — « 0-9 » en dernier. */
  #rebatir(items) {
    items.sort((a, b) => (a.dataset.key < b.dataset.key ? -1 : a.dataset.key > b.dataset.key ? 1 : 0));

    const parLettre = new Map();
    for (const item of items) {
      const lettre = lettreDe(item.dataset.key);
      if (!parLettre.has(lettre)) parLettre.set(lettre, []);
      parLettre.get(lettre).push(item);
    }

    const ordre = [...parLettre.keys()].sort((a, b) => {
      if (a === CHIFFRES) return 1;
      if (b === CHIFFRES) return -1;
      return a < b ? -1 : 1;
    });

    const fragments = ordre.map((lettre) => {
      const slug = lettre.toLowerCase();
      const groupe = document.createElement('section');
      groupe.className = 'merci-brands__group';
      groupe.id = `marques-${slug}`;
      groupe.dataset.letter = lettre;
      groupe.setAttribute('aria-labelledby', `marques-titre-${slug}`);

      const titre = document.createElement('h2');
      titre.className = 'merci-brands__initial';
      titre.id = `marques-titre-${slug}`;
      titre.textContent = lettre;

      const liste = document.createElement('ul');
      liste.className = 'merci-brands__list list-unstyled';
      liste.append(...parLettre.get(lettre));

      groupe.append(titre, liste);
      return groupe;
    });

    this.groupes.replaceChildren(...fragments);
  }

  #choisir(onglet) {
    for (const autre of this.onglets) {
      autre.setAttribute('aria-pressed', String(autre === onglet));
    }
    this.#appliquer();
  }

  /** Masque ce que l'onglet courant exclut, et tient l'alphabet à jour. */
  #appliquer() {
    const actif = this.onglets.find((onglet) => onglet.getAttribute('aria-pressed') === 'true');
    const categorie = actif?.dataset.category || '';

    const presentes = new Set();
    for (const groupe of this.groupes.querySelectorAll('.merci-brands__group')) {
      let visibles = 0;
      for (const item of groupe.querySelectorAll('.merci-brands__item')) {
        const garde = !categorie || item.dataset.categories.split('|').includes(categorie);
        item.hidden = !garde;
        if (garde) visibles++;
      }
      groupe.hidden = visibles === 0;
      if (visibles > 0) presentes.add(groupe.dataset.letter);
    }

    // Une lettre vide n'est pas un lien : sans `href`, l'élément sort de l'ordre
    // de tabulation et n'est plus annoncé comme lien, sans changer de place.
    for (const lien of this.lettres) {
      const lettre = lien.dataset.letter;
      if (presentes.has(lettre)) {
        lien.setAttribute('href', `#marques-${lettre.toLowerCase()}`);
        lien.removeAttribute('aria-disabled');
      } else {
        lien.removeAttribute('href');
        lien.setAttribute('aria-disabled', 'true');
      }
    }
  }
}

if (!customElements.get('merci-brand-index')) {
  customElements.define('merci-brand-index', MerciBrandIndex);
}
